import { RefreshRunState } from '@atlas/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { apiErrorMessage, apiErrorStatus } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { type RefreshRunStatus, workOrderSyncApi } from '@/services/work-order-sync.service'
import { invalidateWorkOrders } from './useWorkOrders'

/** How often the page asks how a refresh is going. */
export const REFRESH_POLL_MS = 2000
/** How long the page waits on a refresh before it stops asking. */
export const REFRESH_TIMEOUT_MS = 120_000
/** How long the button stays disabled after the API said the bundle is already syncing. */
export const ALREADY_SYNCING_HOLD_MS = 30_000

/** Why the button is refused after a 409, when the server's answer carries no message. */
export const ALREADY_SYNCING = 'Already syncing from Workfront'

const HTTP_CONFLICT = 409

/** The queries a refresh can change besides the order itself: the rows the sync writes elsewhere. */
const SYNC_WRITTEN_KEYS = [['work-order-children'], ['work-order-comments']]

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Polls a run until it ends, the deadline passes, or the page leaves. Returns the last status
 * seen, so a run still `running` at the end means the wait timed out.
 */
async function pollRun(
	workOrderId: string,
	runId: string,
	{ intervalMs, timeoutMs, isLive }: { intervalMs: number; timeoutMs: number; isLive: () => boolean },
): Promise<RefreshRunStatus> {
	const deadline = Date.now() + timeoutMs
	for (;;) {
		await sleep(intervalMs)
		const status = await workOrderSyncApi.refreshRun(workOrderId, runId)
		if (status.state !== RefreshRunState.RUNNING || Date.now() + intervalMs > deadline || !isLive()) return status
	}
}

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`

/** The run's outcome, said once through the status bar. */
function report(status: RefreshRunStatus): void {
	switch (status.state) {
		case RefreshRunState.APPLIED:
			notification.success(`Updated from Workfront: ${plural(status.fieldsChanged ?? 0, 'field')} changed`)
			return
		case RefreshRunState.UNCHANGED:
			notification.info('Already up to date')
			return
		case RefreshRunState.FAILED:
			notification.error('The refresh from Workfront failed. Try again in a few minutes.')
			return
		default:
			notification.warning('Workfront is still syncing this work order. Check back in a few minutes.')
	}
}

/**
 * Refreshes a Workfront work order's whole bundle and reports how it went.
 *
 * A command, not an optimistic mutation: the page cannot know what Workfront holds, so it
 * shows *Refreshing…* while the run goes, polling it every {@link REFRESH_POLL_MS} for up to
 * {@link REFRESH_TIMEOUT_MS}, then reports through the status bar and refetches the order.
 * A 409 means the adapter will not take the request now (the bundle is already syncing, or
 * on-demand sync is turned off): the button is held disabled for a while with the server's
 * reason, rather than queueing a second run.
 */
export function useRefreshFromWorkfront(
	workOrderId: string,
	{ intervalMs = REFRESH_POLL_MS, timeoutMs = REFRESH_TIMEOUT_MS } = {},
) {
	const qc = useQueryClient()
	const live = useRef(true)
	// The server's reason for the last 409, while the button is held; null when it is not.
	const [heldReason, setHeldReason] = useState<string | null>(null)

	useEffect(() => {
		live.current = true
		return () => {
			live.current = false
		}
	}, [])

	useEffect(() => {
		if (heldReason === null) return
		const timer = setTimeout(() => setHeldReason(null), ALREADY_SYNCING_HOLD_MS)
		return () => clearTimeout(timer)
	}, [heldReason])

	const mutation = useMutation({
		mutationFn: async () => {
			const { runId } = await workOrderSyncApi.refreshFromWorkfront(workOrderId)
			return pollRun(workOrderId, runId, { intervalMs, timeoutMs, isLive: () => live.current })
		},
		onSuccess: (status) => {
			setHeldReason(null)
			report(status)
		},
		onError: (error) => {
			if (apiErrorStatus(error) === HTTP_CONFLICT) {
				// The server's own reason when it gave one; never the transport's "status code 409".
				const reason =
					isAxiosError(error) && error.response?.data ? apiErrorMessage(error, ALREADY_SYNCING) : ALREADY_SYNCING
				setHeldReason(reason)
				notification.warning(reason)
				return
			}
			notification.error(apiErrorMessage(error, 'Could not refresh from Workfront.'))
		},
		onSettled: () => {
			invalidateWorkOrders(qc)
			for (const queryKey of SYNC_WRITTEN_KEYS) void qc.invalidateQueries({ queryKey })
		},
	})

	return {
		refresh: () => mutation.mutate(),
		isRefreshing: mutation.isPending,
		/** True while the button is held after a 409. */
		alreadySyncing: heldReason !== null,
		/** Why it is held: the server's message for the 409. */
		heldReason: heldReason ?? ALREADY_SYNCING,
	}
}

/** The sync actions recorded for a bundle's latest version, for the bundle detail. Admin only. */
export const useWorkOrderSyncActions = (bundleId: string, version?: number) =>
	useQuery({
		queryKey: ['work-order-sync-actions', bundleId, version ?? 'latest'],
		queryFn: () => workOrderSyncApi.syncActions(bundleId, version),
		enabled: !!bundleId,
	})
